<template>
  <v-panel class="device-list">
    <template v-slot:header>
      <slot></slot>
    </template>

    <table class="table">
      <thead>
        <tr>
          <th class="action">
            <v-checkbox :value="areAllDevicesSelected" @change="toggleSelectedDevices"></v-checkbox>
          </th>
          <th class="position" v-if="!isEditable">{{ $t('Position') }}</th>
          <th>{{ $t('Name') }}</th>
          <th v-if="isEditable" class="more">
            <v-icon name="more-vertical"></v-icon>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(device, index) in devices" :key="index">
          <td>
            <v-checkbox :value="isDeviceSelected(device)" @change="toggleDevice(device, $event)"></v-checkbox>
          </td>
          <td v-if="!isEditable">
            {{ device.id }}
          </td>
          <td>
            {{ device.name }}
          </td>
          <td v-if="isEditable">
            <v-popover class="edit-actions" placement="bottom" :offset="[-50, 15]">
              <button class="secondary rounded small icon">
                <v-icon name="settings"></v-icon>
                <v-icon name="chevron-down"></v-icon>
              </button>
              <template v-slot:popover>
                <ul class="nav">
                  <li>
                    <button class="secondary small icon" @click="editDevice(device)">
                      <v-icon name="edit"></v-icon>
                      {{ $t('Edit') }}
                    </button>
                  </li>
                  <li>
                    <button class="small danger icon" @click="removeDevices([device])">
                      <v-icon name="x"></v-icon>
                      {{ $t('Delete') }}
                    </button>
                  </li>
                </ul>
              </template>
            </v-popover>
          </td>
        </tr>
      </tbody>
    </table>

    <template v-slot:footer>
      <ul class="actions flex flex-center">
        <li>
          <button class="success" @click="addDevices">{{ $t('Add Devices') }}</button>
        </li>
        <li v-if="isEditable">
          <button class="danger" :disabled="!hasSelectedDevices" @click="removeDevices(selectedDevices)">
            {{ $t('Delete Selected') }}
          </button>
        </li>
      </ul>
    </template>
  </v-panel>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { $t } from '/@/i18n'
import { EthercatDevice } from '/@/shared/entities/EthercatDevice'

interface Props {
  devices?: EthercatDevice[]
  isEditable?: boolean
}

interface Emits {
  (e: 'add-devices', devices: EthercatDevice[]): void
  (e: 'remove-devices', devices: EthercatDevice[]): void
  (e: 'edit-device', device: EthercatDevice): void
}

const props = withDefaults(defineProps<Props>(), {
  devices: () => [],
  isEditable: false,
})

const emit = defineEmits<Emits>()

const selectedDevices = ref<EthercatDevice[]>([])

watch(
  () => props.devices,
  (newDevices) => {
    selectedDevices.value = [...newDevices]
  },
  { deep: true },
)

const hasSelectedDevices = computed(() => selectedDevices.value.length > 0)

const areAllDevicesSelected = computed(() => selectedDevices.value.length === props.devices.length)

const isDeviceSelected = (device: EthercatDevice) => {
  return selectedDevices.value.includes(device)
}

const toggleDevice = (device: EthercatDevice, event: Event) => {
  const checkbox = event.target as HTMLInputElement

  if (checkbox.checked) {
    selectedDevices.value.push(device)
  } else {
    selectedDevices.value.splice(selectedDevices.value.indexOf(device), 1)
  }
}

const toggleSelectedDevices = (event: Event) => {
  const checkbox = event.target as HTMLInputElement

  if (!props.devices) {
    return
  }

  if (!checkbox.checked) {
    selectedDevices.value = []
  } else {
    selectedDevices.value = [...props.devices]
  }
}

const removeDevices = (devices: EthercatDevice[]) => {
  emit('remove-devices', devices)
  selectedDevices.value = []
}

const addDevices = () => {
  emit('add-devices', [...selectedDevices.value])
}

const editDevice = (device: EthercatDevice) => {
  emit('edit-device', device)
}
</script>

<style src="./style.css"></style>
